import { createTheme, alpha } from '@mui/material/styles';
import { getPalette } from './palette';
import { typography } from './typography';

const SHADOW_LIGHT = 'rgba(15, 23, 42, 0.05)';
const SHADOW_LIGHT_MD = 'rgba(15, 23, 42, 0.08)';
const SHADOW_LIGHT_LG = 'rgba(15, 23, 42, 0.12)';

const SHADOW_DARK = 'rgba(0, 0, 0, 0.25)';
const SHADOW_DARK_MD = 'rgba(0, 0, 0, 0.35)';
const SHADOW_DARK_LG = 'rgba(0, 0, 0, 0.50)';

const buildCustomShadows = (isDark) => {
  const sKey = isDark ? SHADOW_DARK : SHADOW_LIGHT;
  const sKeyMd = isDark ? SHADOW_DARK_MD : SHADOW_LIGHT_MD;
  const sKeyLg = isDark ? SHADOW_DARK_LG : SHADOW_LIGHT_LG;

  return [
    'none',
    `0 1px 2px 0 ${sKey}`,
    `0 1px 3px 0 ${sKeyMd}, 0 1px 2px -1px ${sKey}`,
    `0 4px 6px -1px ${sKeyMd}, 0 2px 4px -2px ${sKey}`,
    `0 10px 15px -3px ${sKeyMd}, 0 4px 6px -4px ${sKey}`,
    `0 20px 25px -5px ${sKeyLg}, 0 8px 10px -6px ${sKey}`,
    ...Array(19).fill(`0 25px 50px -12px ${sKeyLg}`),
  ];
};

export const createAppTheme = (mode = 'light') => {
  const palette = getPalette(mode);
  const isDark = mode === 'dark';

  return createTheme({
    palette,
    typography,
    shape: {
      borderRadius: 10,
    },
    shadows: buildCustomShadows(isDark),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            boxSizing: 'border-box',
          },
          body: {
            backgroundColor: palette.background.default,
            color: palette.text.primary,
          },
          '*:focus-visible': {
            outline: `2px solid ${palette.primary.main}`,
            outlineOffset: 2,
          },
          '*::-webkit-scrollbar': { width: 7, height: 7 },
          '*::-webkit-scrollbar-track': {
            background: isDark ? '#1F2937' : '#F1F5F9',
          },
          '*::-webkit-scrollbar-thumb': {
            background: isDark ? '#4B5563' : '#CBD5E1',
            borderRadius: 6,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: isDark ? '#6B7280' : '#94A3B8',
          },
        },
      },

      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${palette.divider}`,
            backgroundColor: palette.background.paper,
            backgroundImage: 'none',
            boxShadow: isDark
              ? '0 1px 3px 0 rgba(0,0,0,0.3)'
              : '0 1px 3px 0 rgba(15,23,42,0.04), 0 1px 2px -1px rgba(15,23,42,0.02)',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 12,
          },
        },
      },

      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.875rem',
            padding: '8px 16px',
            lineHeight: 1.4,
            transition: 'all 0.15s ease-in-out',
            '&:active': {
              transform: 'scale(0.98)',
            },
          },
          containedPrimary: {
            backgroundColor: palette.primary.main,
            color: '#FFFFFF',
            boxShadow: `0 1px 2px 0 ${alpha(palette.primary.main, 0.25)}`,
            '&:hover': {
              backgroundColor: palette.primary.dark,
              boxShadow: `0 4px 12px 0 ${alpha(palette.primary.main, 0.35)}`,
            },
          },
          outlined: {
            borderColor: palette.divider,
            color: palette.text.primary,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: alpha(palette.primary.main, 0.04),
            },
          },
          outlinedPrimary: {
            borderColor: alpha(palette.primary.main, 0.4),
            color: palette.primary.main,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: alpha(palette.primary.main, 0.08),
            },
          },
          sizeSmall: {
            padding: '5px 12px',
            fontSize: '0.8125rem',
            borderRadius: 7,
          },
          sizeLarge: {
            padding: '10px 22px',
            fontSize: '0.9375rem',
            borderRadius: 9,
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: 8,
            color: palette.text.secondary,
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(palette.text.primary, 0.06),
              color: palette.text.primary,
            },
            '@media (max-width:600px)': {
              minWidth: 42,
              minHeight: 42,
            },
          },
          sizeSmall: {
            padding: 6,
            '@media (max-width:600px)': {
              minWidth: 36,
              minHeight: 36,
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${palette.divider}`,
            padding: '13px 16px',
            fontSize: '0.875rem',
            color: palette.text.primary,
          },
          head: {
            backgroundColor: palette.background.neutral,
            color: palette.text.secondary,
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            borderBottom: `1px solid ${palette.divider}`,
            whiteSpace: 'nowrap',
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.12s ease',
            '&.Mui-selected': {
              backgroundColor: alpha(palette.primary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(palette.primary.main, 0.12),
              },
            },
            '&:hover': {
              backgroundColor: isDark
                ? alpha(palette.grey[400], 0.04)
                : 'rgba(248, 250, 252, 0.8)',
            },
          },
        },
      },

      MuiTableContainer: {
        styleOverrides: {
          root: {
            WebkitOverflowScrolling: 'touch',
            '@media (max-width:600px)': {
              '& table tbody td:last-of-type:not([colspan])': {
                position: 'sticky',
                right: 0,
                zIndex: 2,
                backgroundColor: palette.background.paper,
                boxShadow: `-6px 0 8px -4px ${palette.divider}`,
              },
              '& table thead th:last-of-type:not([colspan])': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
                backgroundColor: palette.background.neutral,
                boxShadow: `-6px 0 8px -4px ${palette.divider}`,
              },
              '& table tbody tr:hover td:last-of-type:not([colspan])': {
                backgroundColor: isDark
                  ? '#1E293B'
                  : '#F1F5F9',
              },
              '& table tbody tr.Mui-selected td:last-of-type:not([colspan])': {
                backgroundColor: isDark
                  ? alpha(palette.primary.main, 0.16)
                  : alpha(palette.primary.main, 0.12),
              },
            },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 6,
            fontSize: '0.8125rem',
            height: 26,
            transition: 'background-color 0.15s ease',
          },
          sizeSmall: {
            height: 22,
            fontSize: '0.75rem',
            borderRadius: 5,
          },
          colorDefault: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)',
            color: palette.text.secondary,
          },
          colorPrimary: {
            backgroundColor: palette.primary.lighter,
            color: palette.primary.dark,
          },
          colorSuccess: {
            backgroundColor: palette.success.lighter,
            color: palette.success.dark,
          },
          colorWarning: {
            backgroundColor: palette.warning.lighter,
            color: palette.warning.dark,
          },
          colorError: {
            backgroundColor: palette.error.lighter,
            color: palette.error.dark,
          },
          colorInfo: {
            backgroundColor: palette.info.lighter,
            color: palette.info.dark,
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: '0.875rem',
            backgroundColor: palette.background.paper,
            '& fieldset': {
              borderColor: palette.divider,
              transition: 'border-color 0.15s ease-in-out',
            },
            '&:hover fieldset': {
              borderColor: `${palette.grey[400]} !important`,
            },
            '&.Mui-focused fieldset': {
              borderColor: `${palette.primary.main} !important`,
              borderWidth: '2px !important',
            },
            '&.Mui-error fieldset': {
              borderColor: `${palette.error.main} !important`,
            },
            // Ensure legend is open for date, datetime-local, and time inputs
            '&:has(input[type="date"]), &:has(input[type="datetime-local"]), &:has(input[type="time"])': {
              '& legend': {
                maxWidth: '100% !important',
              },
            },
          },
          input: {
            padding: '10px 14px',
            '&::placeholder': {
              color: palette.text.disabled,
              opacity: 1,
            },
            '&[type="date"], &[type="datetime-local"], &[type="time"]': {
              cursor: 'pointer',
              colorScheme: isDark ? 'dark' : 'light',
              '&::-webkit-calendar-picker-indicator': {
                cursor: 'pointer',
                opacity: 0.7,
                filter: isDark ? 'invert(1)' : 'none',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 1,
                },
              },
            },
          },
          sizeSmall: {
            '& input': {
              padding: '8px 12px',
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            color: palette.text.secondary,
            '&.Mui-focused': {
              color: palette.primary.main,
            },
            // Auto-shrink label whenever followed by date, datetime-local, or time input
            '&:has(~ .MuiInputBase-root input[type="date"]), &:has(~ .MuiInputBase-root input[type="datetime-local"]), &:has(~ .MuiInputBase-root input[type="time"])': {
              transform: 'translate(14px, -9px) scale(0.75) !important',
              backgroundColor: palette.background.paper,
              padding: '0 4px',
              borderRadius: 4,
              zIndex: 1,
            },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          select: {
            paddingTop: 10,
            paddingBottom: 10,
            borderRadius: 8,
          },
        },
      },

      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          invisible: {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${palette.divider}`,
            boxShadow: isDark
              ? '0 12px 28px -4px rgba(0,0,0,0.6)'
              : '0 12px 28px -4px rgba(15,23,42,0.12), 0 4px 8px -2px rgba(15,23,42,0.06)',
            padding: 4,
            backgroundImage: 'none',
          },
          list: {
            padding: '4px 0',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '2px 4px',
            padding: '8px 12px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: palette.text.primary,
            transition: 'all 0.12s ease',
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, 0.08),
              color: palette.primary.main,
            },
            '&.Mui-selected': {
              backgroundColor: alpha(palette.primary.main, 0.12),
              color: palette.primary.main,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: alpha(palette.primary.main, 0.16),
              },
            },
          },
        },
      },

      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${palette.divider}`,
            boxShadow: isDark
              ? '0 12px 28px -4px rgba(0,0,0,0.6)'
              : '0 12px 28px -4px rgba(15,23,42,0.12), 0 4px 8px -2px rgba(15,23,42,0.06)',
            backgroundImage: 'none',
          },
        },
      },

      MuiDialog: {
        defaultProps: {
          scroll: 'paper',
        },
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${palette.divider}`,
            boxShadow: isDark
              ? '0 25px 50px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)'
              : '0 25px 50px -12px rgba(15,23,42,0.22), 0 10px 15px -3px rgba(15,23,42,0.08)',
            backgroundImage: 'none',
            overflow: 'hidden',
            '@media (max-width:600px)': {
              margin: 12,
              width: 'calc(100% - 24px)',
              maxWidth: 'calc(100% - 24px)',
              borderRadius: 14,
            },
          },
          paperScrollPaper: {
            maxHeight: 'calc(100% - 64px)',
            '@media (max-width:600px)': {
              maxHeight: 'calc(100% - 24px)',
            },
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: typography.h6.fontFamily,
            fontWeight: 800,
            fontSize: '1.125rem',
            padding: '20px 24px 16px 24px',
            letterSpacing: '-0.01em',
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '20px 24px',
          },
          dividers: {
            borderColor: palette.divider,
            padding: '20px 24px',
          },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '14px 24px 18px 24px',
            gap: 10,
          },
        },
      },

      MuiSnackbar: {
        styleOverrides: {
          root: {
            maxWidth: 'calc(100% - 32px)',
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontSize: '0.875rem',
            alignItems: 'center',
            border: '1px solid transparent',
          },
          standardSuccess: {
            backgroundColor: palette.success.lighter,
            color: palette.success.dark,
            borderColor: alpha(palette.success.main, 0.2),
            '& .MuiAlert-icon': { color: palette.success.main },
          },
          standardWarning: {
            backgroundColor: palette.warning.lighter,
            color: palette.warning.darker,
            borderColor: alpha(palette.warning.main, 0.25),
            '& .MuiAlert-icon': { color: palette.warning.main },
          },
          standardError: {
            backgroundColor: palette.error.lighter,
            color: palette.error.dark,
            borderColor: alpha(palette.error.main, 0.2),
            '& .MuiAlert-icon': { color: palette.error.main },
          },
          standardInfo: {
            backgroundColor: palette.info.lighter,
            color: palette.info.dark,
            borderColor: alpha(palette.info.main, 0.2),
            '& .MuiAlert-icon': { color: palette.info.main },
          },
          filledSuccess: {
            backgroundColor: palette.success.main,
            color: '#FFFFFF',
            boxShadow: `0 4px 12px 0 ${alpha(palette.success.main, 0.35)}`,
          },
          filledWarning: {
            backgroundColor: palette.warning.main,
            color: '#FFFFFF',
            boxShadow: `0 4px 12px 0 ${alpha(palette.warning.main, 0.35)}`,
          },
          filledError: {
            backgroundColor: palette.error.main,
            color: '#FFFFFF',
            boxShadow: `0 4px 12px 0 ${alpha(palette.error.main, 0.35)}`,
          },
          filledInfo: {
            backgroundColor: palette.info.main,
            color: '#FFFFFF',
            boxShadow: `0 4px 12px 0 ${alpha(palette.info.main, 0.35)}`,
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? palette.grey[800] : palette.grey[900],
            color: '#FFFFFF',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 500,
            padding: '6px 10px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          },
          arrow: {
            color: isDark ? palette.grey[800] : palette.grey[900],
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44,
          },
          indicator: {
            height: 3,
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            backgroundColor: palette.primary.main,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 44,
            padding: '8px 16px',
            color: palette.text.secondary,
            transition: 'color 0.15s ease',
            '&.Mui-selected': {
              color: palette.primary.main,
            },
            '&:hover': {
              color: palette.text.primary,
            },
          },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            color: palette.grey[400],
            '&.Mui-checked': {
              color: palette.primary.main,
            },
          },
        },
      },

      MuiRadio: {
        styleOverrides: {
          root: {
            color: palette.grey[400],
            '&.Mui-checked': {
              color: palette.primary.main,
            },
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: 2,
              transitionDuration: '200ms',
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: palette.primary.main,
                  opacity: 1,
                  border: 0,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 22,
              height: 22,
            },
            '& .MuiSwitch-track': {
              borderRadius: 13,
              backgroundColor: isDark ? palette.grey[700] : palette.grey[300],
              opacity: 1,
              transition: 'background-color 200ms ease',
            },
          },
        },
      },

      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              borderRadius: 7,
              fontWeight: 600,
              fontSize: '0.8125rem',
              '&.Mui-selected': {
                backgroundColor: palette.primary.main,
                color: '#fff',
                '&:hover': {
                  backgroundColor: palette.primary.dark,
                },
              },
            },
          },
        },
      },

      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${palette.divider}`,
            color: palette.text.secondary,
            overflowX: 'hidden',
          },
          selectLabel: {
            fontSize: '0.8125rem',
            color: palette.text.secondary,
          },
          displayedRows: {
            fontSize: '0.8125rem',
            color: palette.text.secondary,
          },
          select: {
            fontSize: '0.8125rem',
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: palette.divider,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.background.paper,
            backgroundImage: 'none',
            boxShadow: isDark
              ? '0 0 40px rgba(0,0,0,0.6)'
              : '0 0 40px rgba(15,23,42,0.12)',
          },
          paperAnchorLeft: {
            borderRight: `1px solid ${palette.divider}`,
            borderLeft: 'none',
          },
          paperAnchorRight: {
            borderLeft: `1px solid ${palette.divider}`,
            borderRight: 'none',
          },
        },
      },

      MuiAppBar: {
        defaultProps: { color: 'transparent' },
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
      },
    },
  });
};

const theme = createAppTheme('light');
export default theme;
